'use client';

import { Button } from '@/components/ui/common/button';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useContactUsContext from '@/components/providers/context/contact-us-context';
import Hls from 'hls.js';
import Logo from '@/components/ui/navigation/top-menu/logo';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';
import useViewportActivation from '@/app/lib/hooks/use-viewport-activation';

function ContactUs({ locale }: { locale: string }) {
    const { t } = useTranslation('home');
    const { openForm } = useContactUsContext();
    const contactUsRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const { ref: activationRef, isActive: shouldLoadVideo } =
        useViewportActivation<HTMLDivElement>({
            rootMargin: '400px 0px',
            initialActive: true,
        });

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (!shouldLoadVideo) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            video.pause();
            video.removeAttribute('src');
            video.load();
            return;
        }

        const HLS_SRC = `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/earth-hls/index.m3u8`;

        const startPlay = () => {
            video.play().catch(() => {
                /* autoplay blocked, ignore */
            });
        };

        if (Hls.isSupported()) {
            const hls = new Hls();
            hlsRef.current = hls;
            hls.loadSource(HLS_SRC);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, startPlay);
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari 原生支持 HLS
            video.src = HLS_SRC;
            video.addEventListener('loadedmetadata', startPlay, { once: true });
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            video.pause();
            video.removeAttribute('src');
            video.load();
        };
    }, [shouldLoadVideo]);

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
    };

    return (
        <section
            id="contact-us"
            ref={contactUsRef}
            className="relative flex w-full flex-col items-center bg-white"
            style={{ height: 'var(--homepage-vh, 100svh)' }}
        >
            <div
                ref={activationRef}
                className="pointer-events-none absolute inset-0"
            />
            <div className="top-(--menu-height) w-900:left-[24px] w-900:right-[24px] w-900:bottom-[24px] pointer-events-none absolute bottom-[16px] left-0 right-0 z-0 flex items-center justify-center">
                <div className="w-900:rounded-[8px] relative h-full w-full max-w-[1600px] overflow-hidden rounded-none bg-black">
                    <div className="w-900:bg-gradient-to-r w-900:from-black w-900:from-[45%] w-900:via-black/50 w-900:via-[55%] w-900:to-transparent w-900:to-[65%] pointer-events-none absolute inset-0 z-20 bg-gradient-to-b from-black from-30% via-black/80 via-60% to-transparent to-100%"></div>
                    <video
                        ref={videoRef}
                        poster={getStaticAssetUrl('/home/cover-earth.webp')}
                        playsInline
                        muted
                        loop
                        className="w-900:object-center w-900:origin-center w-900:translate-y-0 w-900:translate-x-[50%] w-900:[mask-image:linear-gradient(to_right,transparent_0%,black_10%)] w-900:[-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_10%)] absolute inset-0 z-10 h-full w-full origin-bottom translate-x-0 translate-y-[50%] object-cover object-bottom"
                        onContextMenu={handleContextMenu}
                    />
                </div>
            </div>

            <div
                className={clsx(
                    `tablet:gap-[16px] relative z-10 flex h-full flex-col`,
                    'tablet:max-w-[87.5vw] desktop:max-w-[1440px] w-[87.5vw] max-w-[480px]',
                    'w-900:justify-center justify-start',
                    'w-900:mt-0 mt-[calc(var(--homepage-vh,100svh)*0.2)]',
                )}
            >
                {/* 文字区域 */}
                <div className="tablet:gap-[16px] tablet:pb-[40px] flex flex-col gap-[8px] pb-[32px]">
                    {locale === 'zh' ? (
                        <>
                            <Logo
                                width={100}
                                height={28}
                                color="white"
                                className="tablet:hidden block"
                            />
                            <Logo
                                width={160}
                                height={44}
                                color="white"
                                className="tablet:block desktop:hidden hidden"
                            />
                            <Logo
                                width={200}
                                height={55}
                                color="white"
                                className="desktop:block hidden"
                            />
                        </>
                    ) : null}

                    <div
                        className={clsx(
                            `j-h3 tablet:j-h1 flex flex-col text-white `,
                        )}
                    >
                        {locale !== 'zh' ? (
                            <p className="inline-block whitespace-nowrap">
                                {t('title.part1')}
                            </p>
                        ) : null}
                        <p>
                            {(
                                [
                                    [
                                        t('title.part2a'),
                                        'linear-gradient(90deg,#765EFF 0%,#FFCFAA 100%)',
                                    ],
                                    [
                                        t('title.part2b'),
                                        'linear-gradient(90deg,#8365FF 0%,#F873FF 100%)',
                                    ],
                                ] as [string, string][]
                            )
                                .filter(([seg]) => Boolean(seg))
                                .map(([seg, grad], si) => (
                                    <span
                                        key={si}
                                        className="inline-block whitespace-nowrap"
                                    >
                                        {seg
                                            .split(/\*\*(.*?)\*\*/g)
                                            .map((part, i) =>
                                                i % 2 === 1 ? (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            background: grad,
                                                            WebkitBackgroundClip:
                                                                'text',
                                                            backgroundClip:
                                                                'text',
                                                            WebkitTextFillColor:
                                                                'transparent',
                                                        }}
                                                    >
                                                        {part}
                                                    </span>
                                                ) : (
                                                    part
                                                ),
                                            )}
                                    </span>
                                ))}
                        </p>
                        <p className="whitespace-nowrap">
                            {t('title.part3')
                                .split(/\*\*(.*?)\*\*/g)
                                .map((part, i) =>
                                    i % 2 === 1 ? (
                                        <span
                                            key={i}
                                            style={{
                                                background:
                                                    'linear-gradient(90deg,#8F7EFF 0%,#FFF 100%)',
                                                WebkitBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                                WebkitTextFillColor:
                                                    'transparent',
                                            }}
                                        >
                                            {part}
                                        </span>
                                    ) : (
                                        part
                                    ),
                                )}
                        </p>
                    </div>

                    <div
                        className={clsx(
                            `j-t2 tablet:j-t1 w-900:w-[660px] w-full text-white`,
                        )}
                    >
                        {t('subtitle')}
                    </div>
                </div>

                <div>
                    <Button
                        className={clsx(
                            'j-t1 !tablet:h-[48px] flex !h-[40px] cursor-pointer !gap-[4px] !bg-[#724DF9] !py-[12px] !pl-[24px] !pr-[20px]',
                        )}
                        onClick={openForm}
                        text={t('button')}
                        icon={
                            <svg
                                viewBox="0 0 1024 1024"
                                version="1.1"
                                xmlns="http://www.w3.org/2000/svg"
                                width={18}
                                height={18}
                                fill="#FFFFFF"
                            >
                                <path d="M526.08 106.24l-60.586667 60.586667L768 469.333333H92.586667v85.333334H768l-302.506667 302.506666 60.586667 60.586667 405.333333-405.76z"></path>
                            </svg>
                        }
                    />
                </div>
            </div>
        </section>
    );
}

export default ContactUs;
